import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  CircularProgress,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { StyledDataTable } from "components/molecules/StyledDataTable";
import {
  RightHeaderDiv,
  RightStyledH1,
  CurrentWeekOverview,
  WeekOverview,
  WeekDetails,
  Label,
  TableLabel,
  TopSection,
  TableSection,
  MetricsHead,
  MetricsDetails,
  MetricsData,
  TableHeadTitle,
  CheckTable,
  CallHeadSection,
  VoicemessageTitle,
  VoicemessageTitleLink,
  MetricTableSection,
} from "pages/UserDetails/styles";
import { useEffect, useState } from "react";
import { USAGE_HISTORY_TABLE_COLUMNS, conditionalRowStyles } from "./columns";
import { GET_CALL_RECORDS, GET_CALL_ENGAGEMENT_DURATION } from "./queries";

import {
  UsageHistoryProps,
  UsageHistoryResponse,
  CallEngagementDurationResponse,
} from "./types";
import { secondsToHMS, transformDate } from "utils/date-utils";
import { Box } from "@mui/system";
import { TableColumn } from "react-data-table-component";
import { CallRecord, Playlist, SortingDirection } from "types/api";
import { getEnumName } from "types/utils";
import { difference } from "pages/Users/columns";
import { UserDisplayButton } from "styles/flex";

const DEFAULT_PAGE_SIZE = 20;

export const UsageHistory: React.FC<UsageHistoryProps> = ({
  accountId,
  account,
}) => {
  const [showError, setShowError] = useState<boolean>(false);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [refetching, setRefetching] = useState<boolean>(true);
  const [allCalls, setAllCalls] = useState<boolean>(false);
  // @ts-expect-error removed any, but not sure if null is ok or would break things, so leaving the {}
  const [requiredData, setRequiredData] = useState<UsageHistoryResponse>({});

  const { data, error, refetch, networkStatus, loading } =
    useQuery<UsageHistoryResponse>(GET_CALL_RECORDS, {
      variables: {
        accountId: accountId,
        take: DEFAULT_PAGE_SIZE,
        skip: 0,
        minDuration: 0,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    });

  const {
    data: completedCallData,
    refetch: refetchCompletedData,
    error: completedCallerror,
    networkStatus: completedCallstatus,
  } = useQuery<UsageHistoryResponse>(GET_CALL_RECORDS, {
    variables: {
      accountId: accountId,
      take: DEFAULT_PAGE_SIZE,
      skip: 0,
      minDuration: 15,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const DataPlaylist: Playlist[] | undefined = account?.playlists;
  const ActivePlaylist = DataPlaylist?.find((item) => item?.isActive);

  useEffect(() => {
    if (allCalls) {
      data && setRequiredData(data);
    } else {
      if (completedCallData && ActivePlaylist) {
        const thisWeekCompletedCalls =
          completedCallData.callRecords.data.filter((calls) => {
            return (
              calls.createdOn &&
              ActivePlaylist.startOn &&
              ActivePlaylist.endOn &&
              calls.createdOn > ActivePlaylist.startOn &&
              calls.createdOn < ActivePlaylist.endOn
            );
          });

        if (thisWeekCompletedCalls && ActivePlaylist) {
          const filteredData = { ...completedCallData };
          filteredData.callRecords.data = thisWeekCompletedCalls;
          setRequiredData(filteredData);
        } else {
          // @ts-expect-error This might be here for a reason, but makes the typing a mess
          setRequiredData({ callRecords: { data: [] } });
        }
      } else {
        // @ts-expect-error This might be here for a reason, but makes the typing a mess
        setRequiredData({ callRecords: { data: [] } });
      }
    }
  }, [completedCallData, data, ActivePlaylist, allCalls]);

  useEffect(() => {
    if (allCalls) {
      setRefetching(networkStatus !== NetworkStatus?.ready);
    } else {
      setRefetching(completedCallstatus !== NetworkStatus?.ready);
    }
  }, [networkStatus, completedCallstatus, allCalls]);

  const {
    data: callEngagementDurationData,
    loading: callEngagementDurationLoading,
    error: callEngagementDurationError,
  } = useQuery<CallEngagementDurationResponse>(GET_CALL_ENGAGEMENT_DURATION, {
    variables: { accountId: accountId },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (
      error ||
      networkStatus === NetworkStatus.error ||
      completedCallerror ||
      completedCallstatus === NetworkStatus.error
    ) {
      setShowError(true);
    }
  }, [error, networkStatus, completedCallerror, completedCallstatus]);

  const handlePageChange = (page: number) => {
    const skipNum = page > 1 ? perPage * (page - 1) : 0;

    if (allCalls) {
      refetch({ take: perPage, skip: skipNum, minDuration: 0 });
    } else {
      refetchCompletedData({ take: perPage, skip: skipNum, minDuration: 15 });
    }
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage);

    if (allCalls) {
      refetch({ take: newPerPage, skip: 0, minDuration: 0 });
    } else {
      refetchCompletedData({ take: newPerPage, skip: 0, minDuration: 15 });
    }
  };

  const CustomLoader = () => (
    <Box
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <CircularProgress />
      <Typography>Loading entries...</Typography>
    </Box>
  );

  const handleSort = (
    _column: TableColumn<CallRecord>,
    sortDirection: string,
  ) => {
    const sortingDirection = getEnumName(
      SortingDirection,
      sortDirection.toUpperCase(),
    );

    if (allCalls) {
      refetch({ sortDirection: sortingDirection, skip: 0, minDuration: 0 });
    } else {
      refetchCompletedData({
        sortDirection: sortingDirection,
        skip: 0,
        minDuration: 15,
      });
    }
  };

  const handleRowClicked = async (row: CallRecord) => {
    const downloadUrl = row?.recordingDownloadUrl?.split(
      "https://api.twilio.com/",
    )[1];
    const newWindow = window.open(`/Call-Record/${row.sid}/${downloadUrl}`);

    newWindow &&
      (newWindow["state"] = {
        state: { accountId: accountId, selectedRecord: row },
      });
  };

  const featurePlaylist = ActivePlaylist?.playlistVoiceMessages?.filter(
    (message) => {
      return (
        message?.voiceMessage?.feature !== "Instructions" &&
        message?.voiceMessage?.cognitiveDomains?.[0] !== "Transition"
      );
    },
  );

  const DataUser = account;
  const diff = difference(DataUser);

  const showAllCalls = async () => {
    if (allCalls) {
      // Switching to current week calls (> 15 minutes)
      await refetchCompletedData({ skip: 0, take: perPage, minDuration: 15 });
    } else {
      // Switching to all calls (all durations)
      await refetch({ skip: 0, take: perPage, minDuration: 0 });
    }
    setAllCalls(!allCalls);
    return true;
  };

  // Merge activities data
  const currentData = allCalls ? data : requiredData;
  currentData?.callRecords?.data?.forEach((obj, index) => {
    obj.activities = currentData?.callRecords?.activitiesPlayed?.[index];
  });

  return (
    <>
      <RightHeaderDiv>
        <RightStyledH1>Usage History</RightStyledH1>

        <TopSection>
          <TableSection>
            <TableLabel>
              <strong>Current Week Overview</strong>
            </TableLabel>
            <CurrentWeekOverview>
              <WeekOverview>
                <WeekDetails>
                  <Label>Week</Label>
                  {diff ? diff : "-"}
                </WeekDetails>
                <WeekDetails>
                  <Label>Start date:</Label>
                  {ActivePlaylist?.isActive
                    ? transformDate(ActivePlaylist?.startOn?.toString())
                    : "NR"}
                </WeekDetails>
                <WeekDetails>
                  <Label>End date:</Label>
                  {ActivePlaylist?.isActive
                    ? transformDate(ActivePlaylist?.endOn?.toString())
                    : "NR"}
                </WeekDetails>
              </WeekOverview>
              <WeekOverview>
                {featurePlaylist?.map((message, index) => {
                  return (
                    <WeekDetails key={index}>
                      <Label> HBA {index + 1}</Label>
                      <Tooltip
                        title={
                          <Typography fontSize={14}>
                            {message?.voiceMessage?.title
                              ? message?.voiceMessage?.title
                              : "NR"}
                          </Typography>
                        }
                        placement="top"
                        arrow
                      >
                        {!message?.voiceMessage?.templateLink ? (
                          <VoicemessageTitle>
                            {message?.voiceMessage?.title
                              ? message?.voiceMessage?.title
                              : "NR"}
                          </VoicemessageTitle>
                        ) : (
                          <VoicemessageTitleLink
                            onClick={() =>
                              window.open(message?.voiceMessage?.templateLink)
                            }
                          >
                            {message?.voiceMessage?.title
                              ? message?.voiceMessage?.title
                              : "NR"}
                          </VoicemessageTitleLink>
                        )}
                      </Tooltip>
                    </WeekDetails>
                  );
                })}
              </WeekOverview>
            </CurrentWeekOverview>
          </TableSection>
          <TableSection>
            <TableLabel>
              <strong>Call Metrics Summary</strong>
            </TableLabel>
            <MetricTableSection>
              <MetricsHead>
                <TableHeadTitle>Metric</TableHeadTitle>
                <TableHeadTitle>Calls</TableHeadTitle>
                <TableHeadTitle>Call time</TableHeadTitle>
              </MetricsHead>
              <MetricsDetails>
                <MetricsData>Current week</MetricsData>
                <MetricsData>
                  {completedCallData?.callRecords?.thisWeek?.count ?? "NR"}
                </MetricsData>
                <MetricsData>
                  {secondsToHMS(
                    completedCallData?.callRecords?.thisWeek?.duration,
                  ) ?? "NR"}
                </MetricsData>
              </MetricsDetails>
              <MetricsDetails>
                <MetricsData>Last week</MetricsData>
                <MetricsData>
                  {completedCallData?.callRecords?.lastWeek?.count ?? "NR"}
                </MetricsData>
                <MetricsData>
                  {secondsToHMS(
                    completedCallData?.callRecords?.lastWeek?.duration,
                  ) ?? "NR"}
                </MetricsData>
              </MetricsDetails>
              <MetricsDetails>
                <MetricsData>This month</MetricsData>
                <MetricsData>
                  {completedCallData?.callRecords?.month?.count ?? "NR"}
                </MetricsData>
                <MetricsData>
                  <strong>
                    {secondsToHMS(
                      completedCallData?.callRecords?.month?.duration,
                    ) ?? "NR"}
                  </strong>
                </MetricsData>
              </MetricsDetails>
              <MetricsDetails>
                <MetricsData>All time</MetricsData>
                <MetricsData>
                  {completedCallData?.callRecords?.count ?? ""}
                </MetricsData>
                <MetricsData>
                  {callEngagementDurationLoading ? (
                    "N/R"
                  ) : (
                    <strong>
                      {secondsToHMS(
                        callEngagementDurationData?.callDuration?.callDuration,
                      ) ?? "NR"}
                    </strong>
                  )}
                </MetricsData>
              </MetricsDetails>
            </MetricTableSection>
          </TableSection>
        </TopSection>

        <CallHeadSection>
          <div>
            <TableLabel>
              <strong>{!allCalls ? "Current Week Calls" : "All Calls"}</strong>
            </TableLabel>
            <div style={{ marginBottom: "10px" }}>
              Displayed in Browser Time Zone
            </div>
          </div>
          <UserDisplayButton>
            <Button onClick={showAllCalls} variant="outlined">
              {allCalls ? "Show Current Week Calls" : "Show All Calls"}
            </Button>
          </UserDisplayButton>
        </CallHeadSection>
      </RightHeaderDiv>

      <CheckTable>
        <StyledDataTable
          columns={USAGE_HISTORY_TABLE_COLUMNS}
          data={currentData?.callRecords?.data ?? []}
          pagination
          paginationServer={allCalls}
          paginationPerPage={perPage}
          paginationTotalRows={
            allCalls
              ? data?.callRecords?.count
              : requiredData?.callRecords?.data?.length
          }
          defaultSortFieldId={1}
          defaultSortAsc={false}
          conditionalRowStyles={conditionalRowStyles}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={refetching || loading}
          progressComponent={<CustomLoader />}
          sortServer
          onSort={handleSort}
          onRowClicked={handleRowClicked}
          paginationDefaultPage={1}
          paginationResetDefaultPage={false}
        />
      </CheckTable>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          {error && "Failed to fetch usage history data."}
          {callEngagementDurationError &&
            "Failed to fetch call engagement total duration."}
          {completedCallerror && "Failed to fetch completed call data."}
        </Alert>
      </Snackbar>
    </>
  );
};
